<script lang="ts">
	import { format } from 'svelte-i18n';
	import { ListItem, LiveIndicator, Text } from '@Shelly/shelly-design-system';
	import { devices } from 'src/stores';
	import {
		consumption_by_device_account,
		consumption_device,
		consumption_rooms,
	} from 'src/stores/features/consumption';
	import { rooms } from 'src/stores';
	import { buildConsumptionStores } from 'src/components/EnergyMonitoring/helpers/consumption_helpers';
	import { buildTopConsumers } from 'src/components/EnergyMonitoring/helpers/energy_consumers_helpers';
	import { getEnergyChartEmptyMessage } from 'src/components/EnergyMonitoring/helpers/energy_flow_helpers';
	import EmWidgetShell from '../EmWidgetShell.svelte';
	import EmWidgetState from '../EmWidgetState.svelte';
	import { energyMonitoringDataMode } from '../helpers/store';
	import { setSidePanelState } from 'src/components/Sidepanel/sidepanel-helpers';
	import {
		ENERGY_ADD_DEVICE_HREF,
		hasPowerMeterDevices,
	} from '../helpers/energy_widget_state_helpers';

	export let embedded = false;
	export let groupBy: 'device' | 'room' = 'device';
	export let staticPreview = false;

	const itemLimit = 5;
	$: consumptionStores = buildConsumptionStores({
		rooms: $rooms,
		devices: $devices,
		consumptionRooms: $consumption_rooms,
		consumptionDevice: $consumption_device,
		consumptionByDeviceAccount: $consumption_by_device_account,
	});

	$: liveItems = buildTopConsumers(
		consumptionStores,
		$devices,
		itemLimit,
		groupBy,
		$rooms,
		staticPreview || $energyMonitoringDataMode === 'demo',
	);

	let previewItems: typeof liveItems | undefined;
	$: items = staticPreview ? (previewItems ?? (previewItems = liveItems)) : liveItems;
	$: highestPower = Math.max(...items.map((item) => item.power), 0);
	$: emptyMessage = getEnergyChartEmptyMessage($devices, (key, fallback) =>
		$format(key, { default: fallback }),
	);

	const openConsumer = (itemId: string) => {
		if (staticPreview || groupBy !== 'device' || !$devices[itemId]) return;
		setSidePanelState({ type: 'device', id: itemId, open: true });
	};
</script>

{#snippet consumersList()}
	<div class="em-consumers-content">
		{#if items.length}
			<div class="em-consumers-list">
				{#each items as item (item.id)}
					{@const percent = highestPower ? (item.power / highestPower) * 100 : 0}
					<ListItem
						title={item.title}
						icon={item.icon}
						image={item.image}
						viewType="bar"
						progress={{
							percent,
							title: item.valueLabel,
							color: item.barColor,
							showPercentBar: true,
						}}
						on:click={() => openConsumer(item.id)}
					/>
				{/each}
			</div>
		{:else}
			<EmWidgetState
				kind={hasPowerMeterDevices($devices) ? 'empty' : 'setup'}
				message={emptyMessage}
				minHeight="100%"
				actionHref={!hasPowerMeterDevices($devices) ? ENERGY_ADD_DEVICE_HREF : ''}
				actionLabel={!hasPowerMeterDevices($devices)
					? $format('button_add_device', { default: 'Add device' })
					: ''}
			/>
		{/if}
	</div>
{/snippet}

{#if embedded}
	{@render consumersList()}
{:else}
	<EmWidgetShell
		widgetClass="energy-widget-consumers"
		title={$format('energy_monitoring_top_consumers_title', { default: 'Top consumers' })}
		infoText={$format('energy_monitoring_top_consumers_info', {
			default: 'Devices drawing the most power right now, ranked by current load.',
		})}
	>
		<svelte:fragment slot="actions">
			<LiveIndicator />
		</svelte:fragment>
		{@render consumersList()}
	</EmWidgetShell>
{/if}

<style lang="scss">
	.em-consumers-content {
		display: flex;
		flex: 1;
		align-self: stretch;
		width: 100%;
		min-width: 0;
		min-height: calc(
			10rem + var(--spacing-s) + var(--spacing-s) + var(--spacing-s) + var(--spacing-s)
		);
	}

	.em-consumers-list {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-s);
		flex: 1;
		justify-content: flex-start;
		align-self: stretch;
		width: 100%;
		min-width: 0;
		min-height: 100%;
		overflow-y: auto;
	}

	:global(.em-consumers-list > *) {
		min-height: 2rem;
	}
</style>
